import type { VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { type ReactNode, useTransition } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button, type buttonVariants } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { LoadingSwap } from '@/components/ui+/LoadingSwap';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  // Shown only when the button is in a disabled state
  disabledTooltipText?: string;
  disabledTooltipPosition?: 'bottom' | 'left' | 'right' | 'top' | undefined;
  disabledTooltipClassnames?: string;
  // New props from action-button.tsx
  action?: () => Promise<{ error: boolean; message?: string }>;
  requireAreYouSure?: boolean;
  areYouSureDescription?: ReactNode;
}

export const ActionButton = ({
  disabledTooltipClassnames,
  disabledTooltipPosition,
  disabledTooltipText,
  loading = false,
  action,
  requireAreYouSure = false,
  areYouSureDescription = 'This action cannot be undone.',
  ...props
}: ButtonProps) => {
  const [isTransitionLoading, startTransition] = useTransition();

  // Use transition loading if action is provided, otherwise fall back to loading prop
  const isLoading = action ? isTransitionLoading : loading;

  const handleAction = async () => {
    if (!action) return;
    const data = await action();
    if (data.error) toast.error(data.message ?? 'Error');
  };

  const performAction = () => {
    startTransition(() => {
      void handleAction();
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (action && !requireAreYouSure) {
      performAction();
    }
    props.onClick?.(e);
  };

  // If requireAreYouSure is true, wrap in AlertDialog
  if (requireAreYouSure && action) {
    return (
      <AlertDialog open={isLoading ? true : undefined}>
        <AlertDialogTrigger asChild>
          <Button {...props} />
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>{areYouSureDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={isLoading} onClick={performAction}>
              <LoadingSwap isLoading={isLoading}>Yes</LoadingSwap>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  const button = (
    <Button disabled={isLoading || props.disabled} {...props} onClick={handleClick}>
      <LoadingSwap isLoading={isLoading} className="inline-flex items-center gap-2">
        {props.children}
      </LoadingSwap>
    </Button>
  );

  // If `disabledTooltip` is passed in, wrap the <Button /> in a <Tooltip />.
  if (!!disabledTooltipText && props.disabled) {
    return (
      <Tooltip>
        {/* `asChild` ensures that the TooltipTrigger doesn't render a button, which causes nested buttons */}
        <TooltipTrigger asChild className={disabledTooltipClassnames}>
          <span>{button}</span>
        </TooltipTrigger>
        <TooltipContent side={disabledTooltipPosition ?? 'bottom'} sideOffset={4}>
          {disabledTooltipText}
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
};
